<template>
    <section class="shuttle-links-list" v-if="locations && locations.length">
        <h2 v-if="blok.title" class="title">{{ blok.title }}</h2>
        <div
            class="links-grid"
            :style="{
                '--cols-mobile': blok.columnsMobile || 1,
                '--cols-tablet': blok.columnsTablet || 2,
                '--cols-desktop': blok.columnsDesktop || 4,
            }"
        >
            <template v-for="loc in locations" :key="`${loc.airport.code}-${loc.slug}`">
                <NuxtLink v-if="loc && loc.active" :to="getLocationHref(loc)" class="link" external>
                    {{ getLocationName(loc) }}
                </NuxtLink>
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
// Only accepts processed locations and blok
import type { IShuttleLinkList } from '@nuxt-monorepo/ui/types/components/ShuttleLinkList';
import type { IShuttleLocationData } from '@nuxt-monorepo/ui/types/products/ShuttleProducts';

const { blok, locations } = defineProps<{
    blok: IShuttleLinkList;
    locations: IShuttleLocationData[];
}>();

// Returns a readable location name
const getLocationName = (loc: IShuttleLocationData) => loc.dropoff || loc.pickup || loc.slug;

function toSlug(input: string): string {
    return input
        .trim()
        .toLowerCase()
        .replace(/airport|international/g, '') // remove noisy words if present
        .replace(/[^a-z0-9]+/g, '-') // non-alphanumerics -> hyphen
        .replace(/^-+|-+$/g, ''); // trim hyphens
}

function getAirportPath(airport: IShuttleLocationData['airport']): string {
    const citySlug =
        airport?.city?.code ||
        (airport?.city?.name ? toSlug(airport.city.name) : '') ||
        toSlug(airport?.name || airport?.fullname || '');

    const code = (airport?.code || '').toLowerCase();

    if (!citySlug || !code) {
        return '';
    }
    return `${citySlug}-airport-${code}`;
}

// Generates proper NuxtLink path: "/shuttles/<city>-airport-<code>/l-<slug>"
function getLocationHref(loc: IShuttleLocationData): string {
    const linkTemplate = blok.linksTemplate;
    return linkTemplate.replace('{locationPath}', loc.slug).toLowerCase();
}
</script>

<style scoped>
.shuttle-links-list {
    @apply w-full;
}

.title {
    @apply text-2xl font-semibold mb-6 tracking-tight;
}

.links-grid {
    display: grid;
    grid-template-columns: repeat(var(--cols-mobile), minmax(0, 1fr));
    gap: 0.25rem 2.5rem;
}

/* Tablet: md breakpoint */
@media (min-width: theme('screens.md')) {
    .links-grid {
        grid-template-columns: repeat(var(--cols-tablet), minmax(0, 1fr));
        gap: 0.25rem 3.5rem;
    }
}

/* Desktop: lg breakpoint */
@media (min-width: theme('screens.lg')) {
    .links-grid {
        grid-template-columns: repeat(var(--cols-desktop), minmax(0, 1fr));
        gap: 0.25rem 4.5rem;
    }
}

.link {
    @apply text-base text-link font-medium py-2 px-0 block text-left no-underline hover:underline transition;
}
</style>
